import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { settingsService } from 'v2/shared/services/settings-service'
import type { ApiResponse } from '@shared/types'
import type {
  RTwoFADetailsResponse,
  RTwoFaPhoneDetailsResponse,
  RSendOtpForPhoneNumberChangeResponse,
  RVerifyOtpForPhoneNumberChangeResponse,
  RRecoveryCodes,
  RAuthenticatorAppResponse,
  RVerifyUserResponse,
} from './types'

// GET: 2FA Details
export function useTwoFADetails() {
  return useQuery<ApiResponse<RTwoFADetailsResponse>>({
    queryKey: ['twoFaDetails'],
    queryFn: () => settingsService.getTwoFADetails(),
  })
}

// GET: Phone Details
export function usePhoneDetails() {
  return useQuery<ApiResponse<RTwoFaPhoneDetailsResponse>>({
    queryKey: ['phoneDetails'],
    queryFn: () => settingsService.getTwoFaPhoneDetails(),
  })
}

// POST: Send OTP for Phone Number Change
export function useSendOtpForPhoneNumberChange() {
  return useMutation<
    ApiResponse<RSendOtpForPhoneNumberChangeResponse>,
    unknown,
    { phone_number: string; method: string }
  >({
    mutationFn: (payload) => settingsService.sendOtpForPhoneNumberChange(payload),
  })
}

// POST: Verify OTP for Phone Number Change
export function useVerifyOtpForPhoneNumberChange() {
  const queryClient = useQueryClient()

  return useMutation<
    ApiResponse<RVerifyOtpForPhoneNumberChangeResponse>,
    unknown,
    { otp: string; session_uuid: string }
  >({
    mutationFn: (payload) => settingsService.verifyOtpForPhoneNumberChange(payload),
    onSuccess: () => {
      // Invalidate phone details after successful phone number change
      queryClient.invalidateQueries({ queryKey: ['phoneDetails'] })
    },
  })
}

// POST: Generate Recovery Codes
export function useGenerateRecoveryCodes() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<RRecoveryCodes>, unknown, Record<string, unknown>>({
    mutationFn: (payload) => settingsService.generateRecoveryCodes(payload),
    onSuccess: () => {
      // Invalidate 2FA details after generating recovery codes
      queryClient.invalidateQueries({ queryKey: ['twoFaDetails'] })
    },
  })
}

// POST: Add Authenticator
export function useAddAuthenticator() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<RAuthenticatorAppResponse>, unknown, Record<string, unknown>>({
    mutationFn: (payload) => settingsService.addAuthenticator(payload),
    onSuccess: () => {
      // Invalidate 2FA details after adding authenticator
      queryClient.invalidateQueries({ queryKey: ['twoFaDetails'] })
    },
  })
}

// POST: Remove Authenticator
export function useRemoveAuthenticator() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<RAuthenticatorAppResponse>, unknown, Record<string, unknown>>({
    mutationFn: (payload) => settingsService.removeAuthenticator(payload),
    onSuccess: () => {
      // Invalidate 2FA details after removing authenticator
      queryClient.invalidateQueries({ queryKey: ['twoFaDetails'] })
    },
  })
}

// POST: Update Authenticator to Default
export function useUpdateAuthenticatorToDefault() {
  const queryClient = useQueryClient()

  return useMutation<ApiResponse<RAuthenticatorAppResponse>, unknown, { is_default: boolean }>({
    mutationFn: (payload) => settingsService.updateAuthenticatorToDefault(payload),
    onSuccess: () => {
      // Invalidate 2FA details after updating default authenticator
      queryClient.invalidateQueries({ queryKey: ['twoFaDetails'] })
    },
  })
}

// POST: Verify Authenticator Code
export function useVerifyAuthenticatorCode() {
  const queryClient = useQueryClient()
  return useMutation<
    ApiResponse<RAuthenticatorAppResponse>,
    unknown,
    { otp_code: string; is_setup_otp: boolean }
  >({
    mutationFn: (payload) => settingsService.verifyAuthenticatorCode(payload),
    onSuccess: () => {
      // Invalidate 2FA details after verifying authenticator code
      queryClient.invalidateQueries({ queryKey: ['twoFaDetails'] })
    },
  })
}

// POST: Verify User 2FA OTP Send
export function useVerifyUserTwoFaSendOtp() {
  return useMutation<ApiResponse<RVerifyUserResponse>, unknown, { method: string }>({
    mutationFn: (payload) => settingsService.verifyUser(payload),
  })
}

// POST: Verify User 2FA OTP Verify
export function useVerifyUserTwoFaVerifyOtp() {
  return useMutation<
    ApiResponse<RVerifyUserResponse>,
    unknown,
    { method: string; token: string; session_uuid: string }
  >({
    mutationFn: (payload) => settingsService.verifyUserTwoFaOtp(payload),
  })
}
